'use client';

import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { CaretLeft } from '@phosphor-icons/react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { cn } from '@/lib/utils';

interface LegalSection {
  title: string;
  content: React.ReactNode;
}

interface BreadcrumbItem {
  name: string;
  href: string;
}

interface LegalPageLayoutProps {
  title: string;
  icon: React.ReactNode;
  sections: LegalSection[];
  contactBlock?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
}

export function LegalPageLayout({ title, icon, sections, contactBlock, breadcrumbs }: LegalPageLayoutProps) {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();
  const router = useRouter();
  useEffect(() => { setMounted(true); }, []);
  const isDark = mounted ? resolvedTheme === 'dark' : false;

  return (
    <div className={cn(
      'min-h-screen',
      isDark ? 'bg-[var(--bg-primary)]' : 'bg-[var(--surface-secondary)]'
    )}>
      {/* Sticky compact header — no main site header on these pages, so top-0 */}
      <div className={cn(
        'sticky top-0 z-30 border-b backdrop-blur-md',
        isDark
          ? 'bg-[var(--surface)]/95 border-[var(--border-default)]'
          : 'bg-white/95 border-[var(--border-default)]'
      )}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-12 sm:h-14 flex items-center gap-3">
          <button
            onClick={() => router.back()}
            className="p-1.5 -ml-1.5 rounded-lg hover:bg-[var(--surface-hover)] transition-colors flex-shrink-0"
          >
            <CaretLeft className="w-4 h-4 text-[var(--text-secondary)]" weight="bold" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 bg-[var(--primary-subtle)]">
              {icon}
            </div>
            <h1 className="text-sm sm:text-base font-semibold text-[var(--text-primary)] truncate">
              {title}
            </h1>
          </div>
        </div>
      </div>

      {/* Breadcrumbs — below sticky header */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Хлебные крошки" className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-1">
          <ol className="flex flex-wrap items-center gap-1.5 text-[13px] sm:text-sm">
            {breadcrumbs.map((item, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <li key={item.href} className="flex items-center gap-1.5">
                  {i > 0 && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-[var(--text-tertiary)] shrink-0" aria-hidden="true">
                      <path d="M6 3.5L10.5 8L6 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  {isLast ? (
                    <span className="text-[var(--text-tertiary)] truncate max-w-[200px] sm:max-w-none">{item.name}</span>
                  ) : (
                    <Link href={item.href} className="text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors duration-150 whitespace-nowrap">
                      {item.name}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-12">
        <div className={cn(
          'rounded-2xl border overflow-hidden',
          isDark
            ? 'bg-[var(--surface)] border-[var(--border-default)]'
            : 'bg-white border-[var(--border-default)]'
        )}>
          <div className="p-5 sm:p-8 space-y-8">
            {sections.map((section, i) => (
              <section key={i}>
                <h2 className="text-base sm:text-lg font-semibold text-[var(--text-primary)] mb-3">
                  {section.title}
                </h2>
                <div className="text-sm text-[var(--text-secondary)] leading-relaxed space-y-3">
                  {section.content}
                </div>
              </section>
            ))}
          </div>

          {contactBlock && (
            <div className={cn(
              'px-5 sm:px-8 py-5 border-t',
              'bg-[var(--surface-secondary)] border-[var(--border-default)]'
            )}>
              {contactBlock}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
